import React, {useMemo} from 'react'
import {Box, Text, Button, Link as ChakraLink} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'

/* Utils */
import {generateStyles, getHideStyles} from '../../utils/utils'
import {AuthModal, useAuthModal} from '../../../../hooks/use-auth-modal'

/* Contexts */
import { useGetLinkData } from '../../../../contexts/category-url-context'

const BannerBlock = ({
    Text: BannerText,
    BannerBlockTextStyle,
    CtaLinkText,
    CtaLinkTextStyle,
    BoldCta,
    UnderlineCta,
    Cta,
    BackgroundColor,
    HideOnDevices
}) => {
    const hideStyles = getHideStyles(HideOnDevices)

    if (hideStyles.doNotLoadComponent) return

    const authModal = useAuthModal()
    const getLinkData = useGetLinkData()

    const linkData = useMemo(() => {
        return getLinkData(Cta?.Expanded)
    }, [Cta?.Expanded])

    // Check if the link should open the login modal
    const shouldOpenLoginModal = linkData?.generatedURL === 'navigation_signin'

    // Handle button click
    const handleCtaClick = (e) => {
        if (shouldOpenLoginModal) {
            e.preventDefault()
            authModal.onOpen()
        }
    }

    // Container styles
    const containerStyles = {
        backgroundColor: BackgroundColor || 'transparent',
        margin: '0',
        marginBottom: 8,
        padding: CtaLinkText && linkData?.generatedURL ? '12px 10px' : '0',
        display: 'flex',
        flexDirection: {base: 'column', lg: 'row'},
        justifyContent: {base: 'center', lg: 'space-between'},
        gap: {base: '12px', md: '16px', lg: '20px'}
    }

    // CTA button styles
    const ctaButtonStyles = {
        border: '1px solid black',
        borderRadius: '0',
        padding: '8px 24px',
        maxWidth: '129px',
        height: '40px',
        backgroundColor: 'transparent',
        fontWeight: BoldCta ? 'bold' : 'normal',
        textDecoration: UnderlineCta ? 'underline' : 'none',
        _hover: {
            textDecoration: 'none',
            opacity: 0.8,
            bg: 'black',
            color: 'white'
        }
    }

    const isExternalUrl = linkData?.isExternalUrl || false
    const linkComponent = isExternalUrl ? ChakraLink : RouterLink

    return (
        <>
            <Box {...containerStyles} {...hideStyles} data-block-type="BannerBlock">
                {/* Banner Text */}
                {BannerText && BannerText !== '' && (
                    <Text
                        {...generateStyles(BannerBlockTextStyle, {
                            fontSize: {base: '14px', md: '16px', lg: '18px'},
                            textAlign: 'center',
                            ...(CtaLinkText && linkData?.generatedURL && {maxWidth: '386px'})
                        })}
                    >
                        {BannerText}
                    </Text>
                )}

                {/* CTA Button/Link */}
                {CtaLinkText && linkData?.generatedURL && (
                    <Button
                        as={shouldOpenLoginModal ? undefined : linkComponent}
                        variant="outline"
                        {...(shouldOpenLoginModal ? undefined : isExternalUrl ? { href: linkData?.generatedURL } : { to: linkData?.generatedURL })}
                        {...(!linkData.stayOnSamePage && {
                            target: linkData?.target,
                            rel: linkData?.rel
                        })}
                        onClick={handleCtaClick}
                        alt={linkData.AltText || CtaLinkText}
                        {...generateStyles(CtaLinkTextStyle, {
                            fontSize: {base: '12px', md: '14px', lg: '16px'},
                            color: 'black'
                        })}
                        {...ctaButtonStyles}
                    >
                        {CtaLinkText}
                    </Button>
                )}
            </Box>
            {shouldOpenLoginModal && <AuthModal {...authModal} />}
        </>
    )
}

BannerBlock.propTypes = {
    BannerSize: PropTypes.string,
    Margin: PropTypes.string,
    Text: PropTypes.string,
    BannerBlockTextStyle: PropTypes.object,
    CtaAlignment: PropTypes.string,
    CtaLinkText: PropTypes.string,
    CtaLinkTextStyle: PropTypes.object,
    BoldCta: PropTypes.bool,
    UnderlineCta: PropTypes.bool,
    Cta: PropTypes.object,
    BackgroundColor: PropTypes.string
}

export default BannerBlock
